import { useCallback, useEffect, useState } from 'react';

import {
  ChatSidebar,
  ReportPreview,
  SocketProvider,
  TextChatProvider,
  useReportPreviewModal,
} from '@mint-lib/ai-chat';
import { WelcomeModalDesktop } from '@mint-lib/ai-chat/components';
import { useFlags } from '@mint-lib/routing-context';
import { useRouter } from '@mint/routing';
import { useAtomValue } from 'jotai';

import { Drawer } from '@myn/mui';

import { relatedChatsAtom } from '../../../features/MetadataCollector/useMetadataChats.js';
import { shouldKeepModalOpen } from '../../../hooks/shouldKeepModalOpen.js';
import useAgenticChat from '../../../hooks/useAgenticChat.js';
import useAgenticModal from '../../../hooks/useAgenticModal.js';
import AgenticAiChat from '../../molecules/AgenticAiChat/AgenticAiChat.jsx';
import AgenticAiHeader from '../../molecules/AgenticAiHeader/AgenticAiHeader.jsx';
import AgenticAiModalCompact from './AgenticAiModalCompact.jsx';
import AgenticAiModalFullScreen from './AgenticAiModalFullScreen.jsx';

const COMPACT_CHAT_WIDTH = 660;
const EXPANDED_CHAT_WIDTH = 932;

const AgenticAiDrawer = () => {
  const ff = useFlags('@mint/arm', ['aiWelcomeModal']);
  const { isChatActive, setIsChatManuallyActive } = useAgenticModal();

  const { chatSessionId, resetChat, setChatSessionId } = useAgenticChat();
  const router = useRouter();

  const relatedChats = useAtomValue(relatedChatsAtom);

  const [isChatListExpanded, setIsChatListExpanded] = useState(false);

  const [isFullScreen, setIsFullScreen] = useState(false);

  const { onClose: onCloseReport } = useReportPreviewModal();

  const chatWidth = isChatListExpanded
    ? EXPANDED_CHAT_WIDTH
    : COMPACT_CHAT_WIDTH;

  const onClose = useCallback(() => {
    setIsChatManuallyActive(false);
    setIsChatListExpanded(false);
    setIsFullScreen(false);
    resetChat();
    onCloseReport();
  }, [
    onCloseReport,
    resetChat,
    setIsChatManuallyActive,
    setIsChatListExpanded,
  ]);

  const handleResetChat = useCallback(() => {
    resetChat();
  }, [resetChat]);

  const handleClickItem = useCallback(
    (id: string) => {
      setChatSessionId(id);
      onCloseReport();
    },
    [setChatSessionId, onCloseReport],
  );

  useEffect(() => {
    if (relatedChats.length) {
      setChatSessionId(relatedChats[0].chatSessionId);
    }
  }, [relatedChats, setChatSessionId]);

  useEffect(() => {
    return router.subscribe(
      'onResolved',
      ({ pathChanged, fromLocation, toLocation }) => {
        if (!pathChanged) return;

        if (shouldKeepModalOpen(fromLocation?.pathname, toLocation?.pathname))
          return;

        onClose();
      },
    );
  }, [router, onClose]);

  const ChatModal = isFullScreen
    ? AgenticAiModalFullScreen
    : AgenticAiModalCompact;

  return (
    <SocketProvider chatId={chatSessionId}>
      <TextChatProvider>
        <Drawer
          variant="persistent"
          anchor="right"
          aria-label="agentic-drawer"
          open={isChatActive}
          onClose={onClose}
          PaperProps={{
            sx: {
              boxShadow: '0px 15px 15px -8px rgba(16,24,40,0.15)', // hoverBig
              transition:
                'width 0.2s ease-in-out, transform 225ms cubic-bezier(0, 0, 0.2, 1) !important', // expandable card and default drawer animations
              width: isFullScreen ? '100vw' : `${chatWidth}px`,
              height: isFullScreen ? '100vh' : '748px',
              paddingRight: isFullScreen ? 0 : '20px',
              overflowY: 'hidden',
              overflowX: 'hidden',
              borderRadius: isFullScreen ? 0 : '8px',
              margin: isFullScreen ? 0 : '76px 20px 0 0',
              p: '1px',
              background: isFullScreen
                ? 'var(--ui-primary)'
                : 'linear-gradient(to left bottom, #82F570, #79ECEC)', // ai gradient
            },
          }}
        >
          <ChatModal
            header={
              <AgenticAiHeader
                chatId={chatSessionId}
                onClose={onClose}
                isFullScreen={isFullScreen}
                setIsFullScreen={setIsFullScreen}
              />
            }
            chatList={
              <ChatSidebar
                isExpanded={isChatListExpanded}
                onToggleExpanded={() => setIsChatListExpanded((prev) => !prev)}
                isNewChat={!chatSessionId}
                onClickChatItem={handleClickItem}
                onStartNewChat={handleResetChat}
                onDeleteChat={handleResetChat}
                chatId={chatSessionId}
                withLogo
              />
            }
            chat={<AgenticAiChat isFullScreen={isFullScreen} />}
            reportPreview={
              <ReportPreview isAgentic isAgenticFullScreen={isFullScreen} />
            }
          />
        </Drawer>
        {isChatActive && ff.aiWelcomeModal.enabled && <WelcomeModalDesktop />}
      </TextChatProvider>
    </SocketProvider>
  );
};

export default AgenticAiDrawer;
